// UpgradePlan Page
// Dedicated page for selecting and upgrading billing plans

import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiAlertCircle, FiZap } from 'react-icons/fi';
import { Button } from '../../components/ui/Button/Button';
import { Card } from '../../components/ui/Card/Card';
import { PlanCard } from '../../components/Billing/PlanCard/PlanCard';
import { billingAPI, type AvailablePlan } from '../../api/billing';
import { useBilling } from '../../hooks/useBilling';
import { usePlanUpgrade } from '../../hooks/usePlanUpgrade';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useToast } from '../../hooks/useToast';
import styles from './UpgradePlan.module.css';

import { logger } from '../../utils/logger';
import { clearPricingSelection, parsePricingSelection } from '../../utils/pricingIntent';

export function UpgradePlan() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const initialSelection = parsePricingSelection(search);
  const toast = useToast();
  const showPlanLoadError = toast.error;
  const { currentOrganization } = useOrganization();
  const { billing, loading: billingLoading, error: billingError, refetch: refetchBilling } = useBilling(currentOrganization?.id || '');
  const { upgradePlan, upgrading, error: upgradeError } = usePlanUpgrade();

  const [plans, setPlans] = useState<AvailablePlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState('');
  const [plansAttempt, setPlansAttempt] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(initialSelection?.plan ?? null);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>(initialSelection?.interval ?? 'monthly');
  const [currency, setCurrency] = useState<'usd' | 'eur' | 'czk'>(initialSelection?.currency ?? 'usd');

  useEffect(() => {
    const selection = parsePricingSelection(search);
    if (selection) {
      setSelectedPlan(selection.plan);
      setBillingInterval(selection.interval);
      setCurrency(selection.currency);
      // The query now owns the selection, including reload/back navigation.
      // Future unrelated sign-ins must not restart this purchase journey.
      clearPricingSelection();
    }
  }, [search]);

  // Fetch available plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setPlansLoading(true);
        setPlansError('');
        const response = await billingAPI.getAvailablePlans();
        setPlans(response.plans);
      } catch (error) {
        logger.error('Failed to fetch plans:', error);
        setPlansError('Could not load the available plans. Please try again.');
        showPlanLoadError('Failed to load plans', 'Please try again later');
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, [showPlanLoadError, plansAttempt]);

  const handleSelectPlan = (planType: string) => {
    if (planType !== billing?.plan.type) {
      setSelectedPlan(planType);
    }
  };

  // Backend returns separate plan records for each (type, currency, billing_type) combination
  // Filter by currency AND billing interval
  const filteredPlans = plans.filter((plan) => {
    // Check currency matches
    if (plan.price.currency !== currency) return false;

    // Check billing interval - monthly plans have monthly price > 0, yearly plans have yearly price > 0
    if (billingInterval === 'monthly') {
      return plan.price.monthly > 0;
    } else {
      return plan.price.yearly > 0;
    }
  });

  // Deduplicate by plan type - show each plan type only once
  const uniquePlans = filteredPlans.reduce((acc, plan) => {
    const existingPlan = acc.find(p => p.type === plan.type);
    if (!existingPlan) {
      acc.push(plan);
    }
    return acc;
  }, [] as AvailablePlan[]);

  logger.log('📊 Plans to display:', uniquePlans);

  // Resolve against the current currency/interval instead of retaining a stale
  // record ID when the user changes either control.
  const selectedAvailablePlan = uniquePlans.find(plan => plan.type === selectedPlan);
  const currentPlanType = billing?.plan.type || 'free';
  const hasSelectedPlan = !!selectedAvailablePlan && selectedPlan !== currentPlanType;
  const billingReady = !!currentOrganization?.id && billing?.organization_id === currentOrganization.id
    && !billingLoading && !billingError;
  const canManageBilling = currentOrganization?.user_role === 'owner' || currentOrganization?.user_role === 'admin';
  const canConfirm = hasSelectedPlan && billingReady && canManageBilling && !upgrading && !plansLoading && !plansError;

  const handleUpgrade = async () => {
    if (!canConfirm || !selectedAvailablePlan || !currentOrganization?.id) return;

    logger.log('🚀 Starting upgrade process:', {
      selectedPlan,
      billingInterval,
      currency,
      organizationId: currentOrganization.id,
    });

    try {
      const result = await upgradePlan(
        currentOrganization.id,
        selectedAvailablePlan.type as 'pro' | 'team' | 'enterprise' | 'starter' | 'agency',
        billingInterval,
        currency
      );

      logger.log('📦 Upgrade result received:', result);

      if (result) {
        // Handle currency mismatch error
        if ((result as any).error === 'currency_mismatch') {
          logger.log('❌ Currency mismatch error:', result);
          toast.error(
            'Currency Mismatch',
            (result as any).message || 'Cannot change to a plan with a different currency. Your subscription is locked to the original currency.'
          );
          return;
        }

        // Handle currency lock error
        if ((result as any).error === 'currency_locked') {
          const lockedCurrency = (result as any).locked_currency?.toLowerCase();
          if (lockedCurrency && ['usd', 'eur', 'czk'].includes(lockedCurrency)) {
            logger.log('🔒 Currency locked, auto-selecting:', lockedCurrency);
            setCurrency(lockedCurrency as 'usd' | 'eur' | 'czk');
            toast.warning(
              'Currency Locked',
              `Your account is locked to ${lockedCurrency.toUpperCase()}. Please select ${lockedCurrency.toUpperCase()} as your currency.`
            );
          }
          return;
        }

        // If checkout_url is present, user will be redirected to Stripe
        // The hook handles the redirect, so we don't need to do anything here
        if (result.checkout_url) {
          logger.log('✅ Checkout URL found, redirecting to Stripe...');
          toast.info('Redirecting to payment...', 'Please complete your payment to upgrade');
          // User will be redirected by the hook
        } else {
          logger.log('⚠️ No checkout_url in response, treating as immediate upgrade');
          // For free plan or immediate upgrades (no payment required)
          toast.success('Plan Upgraded!', `Successfully upgraded to ${selectedPlan} plan`);
          await refetchBilling();
          navigate('/admin/billing');
        }
      }
    } catch (error) {
      logger.error('❌ Upgrade failed:', error);
      toast.error('Upgrade Failed', 'Failed to start upgrade process. Please try again.');
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/billing')}
          className={styles.backButton}
        >
          <FiArrowLeft />
          Back to Billing
        </Button>
        
        <div className={styles.headerContent}>
          <div className={styles.headerIcon}>
            <FiZap />
          </div>
          <h1 className={styles.title}>Choose Your Plan</h1>
          <p className={styles.subtitle}>
            Select the plan that best fits your needs. Upgrade or downgrade anytime.
          </p>
        </div>

        {/* Controls Row */}
        <div className={styles.controlsRow}>
          {/* Currency Selector */}
          <div className={styles.currencySelector}>
            <label className={styles.currencyLabel}>Currency:</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as 'usd' | 'eur' | 'czk')}
              className={styles.currencySelect}
            >
              <option value="usd">USD ($)</option>
              <option value="eur">EUR (€)</option>
              <option value="czk">CZK (Kč)</option>
            </select>
          </div>

          {/* Billing Interval Toggle */}
          <div className={styles.intervalToggle}>
            <button
              className={`${styles.intervalButton} ${billingInterval === 'monthly' ? styles.active : ''}`}
              onClick={() => setBillingInterval('monthly')}
            >
              Monthly
            </button>
            <button
              className={`${styles.intervalButton} ${billingInterval === 'yearly' ? styles.active : ''}`}
              onClick={() => setBillingInterval('yearly')}
            >
              Yearly
              <span className={styles.saveBadge}>Save 20%</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {(upgradeError || billingError || plansError) && (
        <Card className={styles.errorCard}>
          <div className={styles.errorContent} role="alert">
            <FiAlertCircle />
            <span>{upgradeError || billingError || plansError}</span>
            {plansError && <Button onClick={() => setPlansAttempt(value => value + 1)}>Try again</Button>}
            {billingError && <Button onClick={() => void refetchBilling()}>Retry billing</Button>}
          </div>
        </Card>
      )}

      {currentOrganization && !canManageBilling && <p role="alert">Only an owner or admin can change this organization's plan.</p>}
      {!plansLoading && !plansError && selectedPlan && !selectedAvailablePlan && (
        <p role="alert">The selected plan is not available for this currency and billing interval. Please choose another option.</p>
      )}
      {billingReady && selectedPlan === currentPlanType && (
        <p role="status">Your organization already uses the {currentPlanType} plan. Select another plan to change it.</p>
      )}

      {/* Plans Grid */}
      <div className={styles.plansGrid}>
        {plansLoading ? (
          <div className={styles.loading}>Loading plans...</div>
        ) : filteredPlans.length === 0 ? (
          <div className={styles.loading}>No plans available for the selected currency.</div>
        ) : (
          uniquePlans.map((plan) => {
            return (
              <div
                key={plan.id}
                className={`${styles.planWrapper} ${selectedAvailablePlan?.id === plan.id ? styles.selected : ''}`}
              >
                <PlanCard
                  plan={plan}
                  billingInterval={billingInterval}
                  isCurrent={plan.is_current || plan.type === currentPlanType}
                  isSelected={selectedAvailablePlan?.id === plan.id}
                  onSelect={() => handleSelectPlan(plan.type)}
                />
              </div>
            );
          })
        )}
      </div>

      {/* Action Footer */}
      {hasSelectedPlan && (
        <div className={styles.footer}>
          <Card className={styles.footerCard}>
            <div className={styles.footerContent}>
              <div className={styles.footerInfo}>
                <h3>Ready to upgrade?</h3>
                <p>You've selected the <strong>{selectedPlan}</strong> plan</p>
                <p>{new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
                  (billingInterval === 'yearly' ? selectedAvailablePlan!.price.yearly : selectedAvailablePlan!.price.monthly) / 100,
                )} per {billingInterval === 'yearly' ? 'year, billed annually' : 'month, billed monthly'}</p>
                {currentOrganization && <p>For {currentOrganization.name}</p>}
              </div>
              <Button
                variant="accent"
                size="lg"
                onClick={handleUpgrade}
                disabled={!canConfirm}
                loading={upgrading}
              >
                {upgrading ? 'Redirecting to payment...' : 'Confirm Upgrade'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

